import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Switch, Table, Button, TreeSelect, message, Tabs, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { createMonthlyTest, updateMonthlyTest, editMonthlyTestMaterials } from '../../../services/testApi';
import type {
  ApiError,
  CategoryTreeNode,
  MonthlyTest,
  MonthlyTestFormValues,
  SelectedMaterial,
  ValidationError,
} from './types';

import { getListTreeForReports } from '../../../services/categoryApi';

const { SHOW_CHILD } = TreeSelect;

const { Text } = Typography;

interface Props {
  test: MonthlyTest | null;
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const MonthlyTestModal: React.FC<Props> = ({ test, visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm<MonthlyTestFormValues>();
  const [loading, setLoading] = useState(false);
  const [selectedMaterialsData, setSelectedMaterialsData] = useState<SelectedMaterial[]>([]);
  const [categoriesTree, setCategoriesTree] = useState<CategoryTreeNode[]>([]);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    getListTreeForReports().then(setCategoriesTree);
    if (test) {
      form.setFieldsValue({
        ...test,
        testTime: Math.ceil(Number(test.testTime || 0) / 60),
      });
      setSelectedMaterialsData(test.materials || []);
    } else {
      form.resetFields();
      setSelectedMaterialsData([]);
    }
  }, [form, test, visible]);

  const handleSave = async () => {
    if (selectedMaterialsData.length === 0) {
      message.error('Пожалуйста, добавьте материалы в тест');
      return;
    }

    const totalQuestions = selectedMaterialsData.reduce((sum, m) => sum + (m.questionsAmount || 0), 0);
    if (totalQuestions === 0) {
      message.error('Количество вопросов в тесте не может быть равно 0');
      return;
    }

    setLoading(true);
    try {
      const values = await form.validateFields();
      const testData = {
        ...values,
        testTime: Number(values.testTime || 0) * 60,
        isActive: !!values.isActive
      };

      let testId = test?.id;
      
      if (testId) {
        await updateMonthlyTest(testId, testData);
      } else {
        const created = await createMonthlyTest(testData);
        testId = created.id;
      }

      if (!testId) {
        message.error('Не удалось найти тест');
        return;
      }

      await editMonthlyTestMaterials(testId, selectedMaterialsData.map(m => ({
        materialId: m.materialId,
        questionsAmount: m.questionsAmount || 0
      })));


      message.success('Тест сохранен успешно');
      onSuccess();
      onCancel();
    } catch (error: unknown) {
      if (isValidationError(error)) return;
      message.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const flattenTree = (nodes: CategoryTreeNode[]): CategoryTreeNode[] => {
    const list: CategoryTreeNode[] = [];
    nodes.forEach(node => {
      const children = node.children;
      if (children && children.length > 0) {
        list.push(...flattenTree(children));
      } else if (node.value.startsWith('material_')) {
        list.push(node);
      }
    });
    return list;
  };

  const handleMaterialsChange = (values: string[]) => {
    const allMaterials = flattenTree(categoriesTree);
    const nextMaterials: CategoryTreeNode[] = [];
    
    values.forEach(val => {
      if (val.startsWith('material_')) {
        const found = allMaterials.find(m => m.value === val);
        if (found) nextMaterials.push(found);
      }
    });

    const uniqueMap = new Map<number, SelectedMaterial>();
    nextMaterials.forEach(m => {
      const id = parseInt(m.value.replace('material_', ''));
      const existing = selectedMaterialsData.find(sm => sm.materialId === id);
      uniqueMap.set(id, {
        materialId: id,
        material: { name: m.label },
        questionsAmount: existing ? existing.questionsAmount : 0,
      });
    });

    setSelectedMaterialsData(Array.from(uniqueMap.values()));
  };

  const columns: TableColumnsType<SelectedMaterial> = [
    { title: 'Материал', dataIndex: ['material', 'name'], key: 'name' },
    {
      title: 'Вопросов',
      dataIndex: 'questionsAmount',
      key: 'questionsAmount',
      render: (val: number, record) => (
        <InputNumber
          min={0}
          precision={0}
          value={val}
          onChange={(newVal) => {
            setSelectedMaterialsData(prev =>
              prev.map(m => m.materialId === record.materialId ? { ...m, questionsAmount: newVal ?? 0 } : m)
            );
          }}
        />
      )
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button 
          icon={<DeleteOutlined />} 
          danger 
          onClick={() => setSelectedMaterialsData(prev => prev.filter(m => m.materialId !== record.materialId))} 
        />
      )
    }
  ];

  const mapTreeData = (nodes: CategoryTreeNode[]): CategoryTreeNode[] =>
    nodes.map(node => ({
      ...node,
      title: node.label,
      children: node.children ? mapTreeData(node.children) : undefined,
    }));

  const treeValue = selectedMaterialsData.map(m => `material_${m.materialId}`);
  const totalQuestions = selectedMaterialsData.reduce((sum, m) => sum + (m.questionsAmount || 0), 0);

  return (
    <Modal
      title={test ? 'Редактировать тест' : 'Новый тест'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={loading}
      width={900}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Основное" key="general">
          <Form form={form} layout="vertical" initialValues={{ isActive: false }}>
            <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Пожалуйста, введите название!' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Описание">
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item name="testTime" label="Время на тест (мин)" rules={[{ required: true, message: 'Обязательно!' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="isActive" label="Активен" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Материалы и вопросы" key="materials">
          <div style={{ marginBottom: 24 }}>
            <Text strong>Добавить материалы: </Text>
            <TreeSelect
              style={{ width: '100%', marginTop: 8 }}
              treeCheckable={true}
              showCheckedStrategy={SHOW_CHILD}

              treeData={categoriesTree.map(c => ({
                ...c,
                title: c.label,
                children: c.children ? mapTreeData(c.children) : undefined,
              }))}
              placeholder="Кликните здесь, чтобы выбрать материалы или категории"
              value={treeValue}
              onChange={handleMaterialsChange}
              treeNodeFilterProp="label"
              multiple
              showSearch
            />

          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Всего вопросов в тесте: </Text>
            <Text style={{ fontSize: 18, color: '#1890ff' }}>{totalQuestions}</Text>

          </div>

          <Table 
            dataSource={selectedMaterialsData} 
            columns={columns} 
            rowKey="materialId" 
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: 'Материалы не выбраны' }}
          />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
};

export default MonthlyTestModal;

const isValidationError = (error: unknown): error is ValidationError => {
  return typeof error === 'object' && error !== null && 'errorFields' in error;
};

const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' && error !== null && 'response' in error;
};

const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    return error.response?.data?.message || 'Ошибка при сохранении';
  }

  return 'Ошибка при сохранении';
};
